import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FaApple, FaGooglePlay } from "react-icons/fa";
import Button from "../ui/Button";

export default function AppDownload() {
  const [isDesktop, setIsDesktop] = useState(true);

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 1024);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <section
      id="download"
      style={{
        padding: "64px 20px 32px 20px",
        background:
          "linear-gradient(135deg, rgba(230, 57, 70, 0.1), rgba(255, 195, 0, 0.1))",
        overflow: "hidden",
      }}
    >
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      <div
        style={{
          maxWidth: "1280px",
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: "48px",
          alignItems: "center",
          justifyItems: "center",
        }}
      >
        {/* LEFT SIDE (TEXT & BUTTONS) */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: "easeOut" }}
          viewport={{ once: true }}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            width: "100%",
          }}
        >
          <span
            style={{
              background: "linear-gradient(to right, #E63946, #FFC300)",
              color: "white",
              padding: "6px 20px",
              borderRadius: "50px",
              fontSize: "clamp(14px, 2vw, 18px)",
              fontWeight: "600",
            }}
          >
            Download Now
          </span>

          <h2
            style={{
              fontSize: "clamp(32px, 6vw, 50px)",
              fontWeight: "bold",
              color: "#1C1C1C",
              margin: "20px 0",
              lineHeight: "1.2",
            }}
          >
            Get the{" "}
            <span
              style={{
                background: "linear-gradient(to right, #E63946, #FFC300)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              INRYDE
            </span>{" "}
            App
          </h2>

          <p
            style={{
              fontSize: "20px",
              color: "#6C757D",
              marginTop: "-6px",
              marginBottom: "28px",
              maxWidth: "500px",
            }}
          >
            Download and get your first ride at a special discount.
            Available on both iOS and Android.
          </p>

          <div style={{ marginBottom: "28px" }}>
            {[
              "🎯 Real-time tracking",
              "💳 Multiple payments",
              "🔒 Verified drivers",
              "⚡ Instant booking",
            ].map((f, i) => (
              <div
                key={i}
                style={{
                  marginBottom: "12px",
                  fontSize: "16px",
                  fontWeight: "500",
                }}
              >
                {f}
              </div>
            ))}
          </div>

          {/* Buttons */}
          <div
            style={{
              display: "flex",
              flexDirection: isDesktop ? "row" : "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "16px",
              marginTop: "8px",
              marginBottom: "24px",
              width: "100%",
            }}
          >
         <a href="https://play.google.com/store/apps/details?id=com.inryde.user&hl=en_IN">   <Button style={{ width: isDesktop ? "auto" : "80%" }}>
              <FaGooglePlay style={{ marginRight: "6px" }} /> Get it on Google
              Play
            </Button></a>
            <Button style={{ width: isDesktop ? "auto" : "80%" }}>
              <FaApple style={{ marginRight: "6px" }} /> Download on App Store
            </Button>
          </div>

          {/* Reviews */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "6px",
              flexWrap: "wrap",
            }}
          >
            {[1, 2, 3, 4, 5].map((i) => (
              <span key={i} style={{ color: "#FFC300", fontSize: "18px" }}>
                ★
              </span>
            ))}
            <span style={{ marginLeft: "6px", color: "#6C757D" }}>
              <strong style={{ color: "#1C1C1C" }}>4.8/5</strong> from 50K+
              reviews
            </span>
          </div>
        </motion.div>

        {/* RIGHT SIDE (MOBILE + FLOATING ICONS) */}
        <motion.div
          initial={{ opacity: 0, x: 100 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 1, ease: "easeOut" }}
          viewport={{ once: true }}
          style={{
            position: "relative",
            display: "inline-block", // ✅ important fix
          }}
        >
          {/* 🏍️ Bike Icon */}
          <div
            style={{
              position: "absolute",
              width: "70px",
              height: "70px",
              background: "linear-gradient(to bottom right, #E63946, #DC2626)",
              borderRadius: "16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "32px",
              color: "white",
              boxShadow: "0 8px 25px rgba(0,0,0,0.2)",
              animation: "spin 5s linear infinite",
              zIndex: 6,
              top: "-20px", // ✅ move freely
              right: "-20px",
            }}
          >
            🏍️
          </div>

          {/* 🚗 Car Icon */}
          <div
            style={{
              position: "absolute",
              width: "70px",
              height: "70px",
              background: "linear-gradient(to bottom right, #FFC300, #FB923C)",
              borderRadius: "16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "32px",
              color: "white",
              boxShadow: "0 8px 25px rgba(0,0,0,0.2)",
              animation: "spin 5s linear infinite",
              zIndex: 6,
              bottom: "-10px",
              left: "-30px", // ✅ freely movable now
            }}
          >
            🚗
          </div>

          {/* ⚡ Tunnel Icon */}
          <div
            style={{
              position: "absolute",
              width: "50px",
              height: "50px",
              background: "linear-gradient(to bottom right, #22C55E, #14B8A6)",
              borderRadius: "16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "24px",
              color: "white",
              boxShadow: "0 8px 20px rgba(0,0,0,0.2)",
              zIndex: 6,
              top: "40%",
              left: "-20px",
            }}
          >
            ⚡
          </div>

          {/* MOBILE FRAME + VIDEO */}
          <motion.div
            animate={{ y: [-6, 0, -6] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            style={{
              width: isDesktop ? "240px" : "180px",
              height: isDesktop ? "480px" : "360px",
              borderRadius: "42px",
              boxShadow: "0 25px 50px rgba(0,0,0,0.3)",
              overflow: "hidden",
              background: "#000",
              display: "flex",
              flexDirection: "column",
              padding: "8px",
              position: "relative",
              zIndex: 5,
            }}
          >
            {/* Top Notch */}
            <div
              style={{
                width: "140px",
                height: "20px",
                background: "#000",
                borderRadius: "0 0 20px 20px",
                margin: "0 auto",
                zIndex: 10,
              }}
            />

            {/* Video */}
            <div
              style={{
                flex: 1,
                borderRadius: "34px",
                overflow: "hidden",
                transform: "translateY(-12px)",
              }}
            >
              <video
                autoPlay
                loop
                muted
                playsInline
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              >
                <source src="/videos/splash_video.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
